import React from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  ContentLayout,
  Header,
  SpaceBetween,
} from "@cloudscape-design/components";
import { getAverageScores } from "../api/api";
import { payloadFromToken } from "../api/auth";
import { PRIVILEGED_USERS } from "../constants/auth";
import { useDangerAlert } from "../hooks/useAlert";
import LeaderboardChart from "../components/LeaderboardChart";

const useAverageScoresReport = ({ setAlert, isPrivileged }) => {
  const [data, setData] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const loadScores = React.useCallback(async () => {
    if (!isPrivileged) return;
    setIsLoading(true);
    setError(null);

    try {
      const result = await getAverageScores();
      setData(result);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [isPrivileged]);

  React.useEffect(() => {
    loadScores();
  }, [loadScores]);

  useDangerAlert(error, setAlert);

  return {
    data,
    isLoading,
    error,
    reload: loadScores,
  };
};

function AdminReportsScores({ setAlert }) {
  const [username, setUsername] = React.useState("");

  React.useEffect(() => {
    const storedToken =
      window.localStorage.getItem("id_token") ||
      window.localStorage.getItem("access_token");

    if (!storedToken) return;

    try {
      const payload = payloadFromToken(storedToken);
      const derivedUsername =
        payload?.["cognito:username"] || payload?.username || payload?.email;
      if (derivedUsername) {
        setUsername(derivedUsername);
      }
    } catch (err) {
      console.debug("Unable to derive username from token", err);
    }
  }, []);

  const isPrivileged = PRIVILEGED_USERS.includes(username);
  const { data, isLoading, reload } = useAverageScoresReport({
    setAlert,
    isPrivileged,
  });

  if (!isPrivileged) {
    return (
      <ContentLayout header={<Header variant="h1">Average Scores</Header>}>
        <Container>
          <Alert
            statusIconAriaLabel="Info"
            header="Access restricted"
            variant="info"
          >
            You do not have permission to view admin reports.
          </Alert>
        </Container>
      </ContentLayout>
    );
  }

  const customerServiceScores =
    data?.customerServiceScores ?? data?.customerService ?? [];
  const salesScores = data?.salesScores ?? data?.sales ?? [];
  const maxScore = data?.maxScore;

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          actions={
            <Button onClick={reload} disabled={isLoading}>
              Refresh data
            </Button>
          }
        >
          Average Scores
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Container
          header={
            <Header variant="h2">
              Average Customer Service Score
            </Header>
          }
        >
          <SpaceBetween size="s">
            <Box color="text-body-secondary">
              Showing the average customer service scores by employee.
            </Box>
            <LeaderboardChart
              title="Average Customer Service Score"
              data={customerServiceScores}
              maxScore={maxScore}
              emptyMessage="No customer service score data available yet."
            />
          </SpaceBetween>
        </Container>
        <Container
          header={
            <Header variant="h2">
              Average Sales Score
            </Header>
          }
        >
          <SpaceBetween size="s">
            <Box color="text-body-secondary">
              Showing the average sales scores by employee.
            </Box>
            <LeaderboardChart
              title="Average Sales Score"
              data={salesScores}
              maxScore={maxScore}
              emptyMessage="No sales score data available yet."
            />
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </ContentLayout>
  );
}

export default AdminReportsScores;
